import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_ORIGIN } from '../config';
import { isCredentialFreeAuthRequest } from '../utils/authApiPaths';
import { getBearerTokenForApi, getStoredPoolSiteToken } from '../utils/poolSiteAuth';

type RetryConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

let refreshPromise: Promise<string> | null = null;

export function getStoredAccessToken(): string | null {
  return localStorage.getItem('token') || localStorage.getItem('auth_token');
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem('refreshToken') || localStorage.getItem('refresh_token');
}

export function persistAccessToken(token: string): void {
  localStorage.setItem('token', token);
  localStorage.setItem('auth_token', token);
}

export function persistRefreshToken(token: string): void {
  localStorage.setItem('refreshToken', token);
  localStorage.setItem('refresh_token', token);
}

export function clearAuthTokens(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('refresh_token');
}

export function dispatchSessionExpired(): void {
  const hadSession = Boolean(getStoredAccessToken() || getStoredRefreshToken());
  clearAuthTokens();
  if (hadSession) {
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  }
}

export function isCredentialAuthFailure(error: AxiosError): boolean {
  const status = error.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  const data = error.response?.data as { detail?: unknown } | undefined;
  const detail =
    typeof data?.detail === 'string' ? data.detail : JSON.stringify(data?.detail ?? '');
  return detail.toLowerCase().includes('credential');
}

/** refresh 전용 — 인터셉터 없는 raw axios */
export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const response = await axios.post<{ access_token?: string; refresh_token?: string }>(
      `${API_ORIGIN}/api/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const accessToken = response.data?.access_token;
    if (!accessToken || typeof accessToken !== 'string') {
      throw new Error('Invalid refresh response');
    }

    persistAccessToken(accessToken);
    const newRefresh = response.data?.refresh_token;
    if (newRefresh && typeof newRefresh === 'string') {
      persistRefreshToken(newRefresh);
    }

    return accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function handleAuthFailureRetry(
  instance: AxiosInstance,
  error: AxiosError
): Promise<unknown> {
  const config = error.config as RetryConfig | undefined;
  if (!config || config._authRetry) {
    return Promise.reject(error);
  }
  if (!isCredentialAuthFailure(error)) {
    return Promise.reject(error);
  }
  if (isCredentialFreeAuthRequest(config.url)) {
    return Promise.reject(error);
  }

  // FormData(첨부 업로드) 재전송 금지 — 토큰 갱신 후 retry 시 파일이 두 번 올라감
  const body = config.data;
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return Promise.reject(error);
  }
  const ct = String(
    (config.headers as Record<string, unknown> | undefined)?.['Content-Type'] ??
      (config.headers as Record<string, unknown> | undefined)?.['content-type'] ??
      ''
  ).toLowerCase();
  if (ct.includes('multipart/form-data')) {
    return Promise.reject(error);
  }

  if (getStoredPoolSiteToken() && !getStoredAccessToken()) {
    // 현장 토큰만 있는 경우 401이어도 세션을 지우지 않음.
    // (관리자 전용 API가 현장 JWT를 거부해도 유효한 현장 로그인이 날아가
    //  이어서 게시판 등이 "Not authenticated" 가 되던 문제)
    return Promise.reject(error);
  }

  try {
    const newToken = await refreshAccessToken();
    config._authRetry = true;
    if (config.headers) {
      config.headers.Authorization = `Bearer ${newToken}`;
    }
    return instance.request(config);
  } catch {
    dispatchSessionExpired();
    return Promise.reject(error);
  }
}

export function attachAuthInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    const token = getBearerTokenForApi();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => handleAuthFailureRetry(instance, error)
  );
}
